/**
 * `<team-history>`: one team's score round by round, opened from the
 * scoreboard beside the table.
 *
 * The round in progress is shown alongside the scored ones as an estimate -
 * what the team's table is worth so far - set in italics, since the cards still
 * held have yet to be counted against it. The total is the banked score only.
 */

import type { GameView } from "@ryzun/haf-saloon-game-engine";
import type { TeamColor } from "../../server/protocol.js";
import { h, toNodes } from "../dom.js";
import { TEAM_COLOR_LABELS } from "../palette.js";
import { getTableEstimate, getTeamName } from "../table-text.js";
import { BaseElement } from "./base-element.js";
import styles from "./team-history.css";

export interface HistoryModel {
    readonly view: GameView;
    readonly teamId: string;
    readonly color: TeamColor;
}

/** Screen readers get the word the italics stand for. */
function estimated(text: string): HTMLElement {
    return h("span", { class: "estimate" }, text, h("span", { class: "sr-only" }, " estimated"));
}

export class TeamHistory extends BaseElement {
    #model: HistoryModel | null = null;

    constructor() {
        super(styles);
    }

    set model(value: HistoryModel | null) {
        this.#model = value;
        this.invalidate();
    }

    protected render(): void {
        const model = this.#model;
        const team = model?.view.teams.find((entry) => entry.id === model.teamId);

        if (model === null || team === undefined) {
            this.root.replaceChildren();
            return;
        }

        const { view } = model;
        const estimate = getTableEstimate(view, team.id);

        this.style.setProperty("--team", `var(--team-${model.color})`);

        const scoredRows = view.scores.map((scored) => {
            const entry = scored.teams.find((candidate) => candidate.teamId === team.id);

            return h(
                "tr",
                {},
                h("th", { scope: "row" }, String(scored.round)),
                h("td", {}, String(entry?.bookBonus ?? 0)),
                h("td", {}, String(entry?.tableValue ?? 0)),
                h("td", {}, entry === undefined || entry.handPenalty === 0 ? "0" : `-${entry.handPenalty}`),
                h("td", {}, String(entry?.total ?? 0))
            );
        });

        const estimateRow = estimate === null
            ? null
            : h(
                "tr",
                { class: "estimate" },
                h("th", { scope: "row" }, estimated(String(view.round))),
                h("td", {}, String(estimate.bookBonus)),
                h("td", {}, String(estimate.tableValue)),
                h("td", {}, h("span", { "aria-label": "not yet counted" }, "—")),
                h("td", {}, estimated(String(estimate.total)))
            );

        this.root.replaceChildren(...toNodes([
            h("h2", { class: "heading" }, `${TEAM_COLOR_LABELS[model.color]} Team`),
            h("p", { class: "members" }, getTeamName(view, team.id)),
            h(
                "table",
                {},
                h("thead", {}, h(
                    "tr",
                    {},
                    h("th", { scope: "col" }, "Round"),
                    h("th", { scope: "col" }, "Books"),
                    h("th", { scope: "col" }, "Melds"),
                    h("th", { scope: "col" }, "Held"),
                    h("th", { scope: "col" }, "Score")
                )),
                h("tbody", {}, ...scoredRows, estimateRow),
                h("tfoot", {}, h(
                    "tr",
                    {},
                    h("th", { scope: "row", colspan: 4 }, "Total"),
                    h("td", {}, String(team.score))
                ))
            ),
            estimate === null
                ? null
                : h("p", { class: "note" }, "The round in progress is an estimate: it counts only what is on the table, not the cards still held, and is not part of the total yet.")
        ]));
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "team-history": TeamHistory;
    }
}

customElements.define("team-history", TeamHistory);
